import { createContext, useCallback, useContext, useEffect, useMemo, useState } from 'react';
import type { ReactNode } from 'react';
import { toBcp47, toShortCode, type LanguageCode } from '@s2w/types';
import i18n, { fallbackLanguage, supportedLanguages } from './index';

interface LanguageContextValue {
  readonly language: LanguageCode;
  readonly bcp47: string;
  readonly supportedLanguages: readonly LanguageCode[];
  readonly setLanguage: (next: string) => void;
}

const LanguageContext = createContext<LanguageContextValue | null>(null);

const resolveSupported = (value: string): LanguageCode | null => {
  const short = toShortCode(value);
  return short && supportedLanguages.includes(short) ? short : null;
};

export interface LanguageProviderProps {
  readonly children: ReactNode;
}

export const LanguageProvider = ({ children }: LanguageProviderProps) => {
  const [language, setLanguageState] = useState<LanguageCode>(
    () => resolveSupported(i18n.language) ?? fallbackLanguage,
  );

  useEffect(() => {
    const onChange = (lng: string) => {
      const next = resolveSupported(lng);
      if (next && next !== language) setLanguageState(next);
    };
    i18n.on('languageChanged', onChange);
    return () => {
      i18n.off('languageChanged', onChange);
    };
  }, [language]);

  const setLanguage = useCallback((next: string) => {
    const resolved = resolveSupported(next);
    if (!resolved) return;
    if (i18n.language === resolved) return;
    void i18n.changeLanguage(resolved);
  }, []);

  useEffect(() => {
    if (typeof document === 'undefined') return;
    document.documentElement.lang = language;
  }, [language]);

  const value = useMemo<LanguageContextValue>(
    () => ({
      language,
      bcp47: toBcp47(language),
      supportedLanguages,
      setLanguage,
    }),
    [language, setLanguage],
  );

  return <LanguageContext value={value}>{children}</LanguageContext>;
};

export const useLanguage = (): LanguageContextValue => {
  const ctx = useContext(LanguageContext);
  if (ctx === null) {
    throw new Error('useLanguage must be used within a LanguageProvider');
  }
  return ctx;
};

/**
 * Read the current language imperatively (outside React). Used by the API
 * client to attach an Accept-Language header on every request.
 */
export const getCurrentLanguage = (): LanguageCode =>
  resolveSupported(i18n.language) ?? fallbackLanguage;

export const getCurrentBcp47 = (): string => toBcp47(getCurrentLanguage());
